import dayjs from "dayjs";
import { useMemo } from "react";
import { ActivityTimelineItem } from "@/components/ActivityTimeline";
import { useAuditLogList } from "@/queries/practice-setting/audit-log/use-audit-log-list";
import { getXtenantId } from "@/utils/utils";
import {
  GROUP_HISTORY_DATE_FORMAT,
  THERAPIST_HISTORY_LOG_TYPE,
  THERAPIST_HISTORY_PAGE_SIZE,
  THERAPIST_HISTORY_SORT_BY,
  THERAPIST_HISTORY_SORT_DIRECTION,
} from "../group-visit-constant";
import { AuditLogRow } from "../group-visit-types";
import { joinDetailParts } from "../group-visit-utils";

export type UseTherapistChangeHistory = {
  items: ActivityTimelineItem[];
  isLoading: boolean;
};

/**
 * Therapist change history from the audit log, newest first.
 *
 * @author Rajkumar Rathod
 */
export const useTherapistChangeHistory = (): UseTherapistChangeHistory => {
  const { data, isLoading } = useAuditLogList({
    pagination: { page: 0, pageSize: THERAPIST_HISTORY_PAGE_SIZE },
    sortBy: THERAPIST_HISTORY_SORT_BY,
    sortDirection: THERAPIST_HISTORY_SORT_DIRECTION,
    logType: THERAPIST_HISTORY_LOG_TYPE,
    xTENANTID: getXtenantId(),
  });

  const items = useMemo<ActivityTimelineItem[]>(() => {
    const rows = (data?.data?.content ?? []) as AuditLogRow[];

    return rows.map((row, index) => ({
      id: row.uuid ?? row.id ?? `${THERAPIST_HISTORY_LOG_TYPE}-${index}`,
      title: row.activity ?? row.action ?? "",
      meta: joinDetailParts([
        row.logUser,
        row.logTime
          ? dayjs(row.logTime).format(GROUP_HISTORY_DATE_FORMAT)
          : undefined,
      ]),
    }));
  }, [data]);

  return { items, isLoading };
};
